import type {
  SiteSettings,
  UserConfig,
  TemplateExtraPage,
  Template,
} from '@/types/builder';
import { BUILDER_INTERFACE_LOCAL_STORAGE_KEYS } from '@/constants/localStorage';
import { applyLogoToTemplate } from '@/utils/builder/logo-utils';

// Get all storage keys for a landing page
const getAllPageKeys = (
  landingPageID: string,
  extraPages: TemplateExtraPage[] = []
): string[] => {
  const keys = [`${BUILDER_INTERFACE_LOCAL_STORAGE_KEYS}-${landingPageID}`];

  extraPages.forEach((page) => {
    keys.push(
      `${BUILDER_INTERFACE_LOCAL_STORAGE_KEYS}-${landingPageID}-${page.title}`
    );
  });

  const prefix = `${BUILDER_INTERFACE_LOCAL_STORAGE_KEYS}-${landingPageID}-`;
  const knownPageKeys = new Set(keys);

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix) && !knownPageKeys.has(key)) {
      keys.push(key);
    }
  }

  return keys;
};

// Update general settings across all page settings for a landing page
export const updateGeneralSettingsAcrossPages = (
  landingPageID: string,
  newGeneralSettings: SiteSettings,
  extraPages: TemplateExtraPage[] = []
): void => {
  const pageKeys = getAllPageKeys(landingPageID, extraPages);

  pageKeys.forEach((storageKey) => {
    try {
      const rawState = localStorage.getItem(storageKey);
      if (!rawState) return;

      const pageState = JSON.parse(rawState);
      const updatedSiteSettings = {
        ...pageState.siteSettings,
        title: newGeneralSettings.title,
        language: newGeneralSettings.language,
        description: newGeneralSettings.description,
        showPageOnSearchEngine: newGeneralSettings.showPageOnSearchEngine,
        customCode: newGeneralSettings.customCode,
      };

      const updatedPageState = {
        ...pageState,
        siteSettings: updatedSiteSettings,
      };
      localStorage.setItem(storageKey, JSON.stringify(updatedPageState));
    } catch (error) {
      console.error(
        `❌ Failed to update page settings for ${storageKey}:`,
        error
      );
    }
  });
};

// Update global settings (userConfig) across all pages for a landing page
export const updateGlobalSettingsAcrossPages = (
  landingPageID: string,
  newUserConfig: UserConfig,
  extraPages: TemplateExtraPage[] = []
): void => {
  const pageKeys = getAllPageKeys(landingPageID, extraPages);

  pageKeys.forEach((storageKey) => {
    try {
      const rawState = localStorage.getItem(storageKey);
      if (!rawState) return;

      const pageState = JSON.parse(rawState);
      const oldLogo = pageState.userConfig?.logo;
      const newLogo = newUserConfig.logo;
      const logoChanged = JSON.stringify(oldLogo) !== JSON.stringify(newLogo);

      const updatedUserConfig = {
        ...pageState.userConfig,
        businessName: newUserConfig.businessName,
        logo: newUserConfig.logo,
        globalConfig: newUserConfig.globalConfig,
      };

      let updatedPageState = { ...pageState, userConfig: updatedUserConfig };

      // Apply logo changes to template and popups if logo changed
      if (logoChanged && pageState.template) {
        const updatedTemplate = {
          ...pageState.template,
          elements: applyLogoToTemplate(
            pageState.template.elements,
            newLogo,
            newUserConfig.businessName
          ),
        };

        const updatedPopupTemplates = (pageState.popupTemplates || []).map(
          (popup: Template) => ({
            ...popup,
            elements: applyLogoToTemplate(
              popup.elements,
              newLogo,
              newUserConfig.businessName
            ),
          })
        );

        updatedPageState = {
          ...updatedPageState,
          template: updatedTemplate,
          popupTemplates: updatedPopupTemplates,
        };
      }

      localStorage.setItem(storageKey, JSON.stringify(updatedPageState));
    } catch (error) {
      console.error(`❌ Failed to update ${storageKey}:`, error);
    }
  });
};
